import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import type { ReactNode } from 'react';
import { useMemo } from 'react';

export type DropdownProps<T> = {
  options: T[];
  getKey: (value: T) => string;
  getLabel: (value: T) => string;
  selected: T | undefined;
  onChange: (value: T) => void;
  disabled?: boolean;
  getDescription?: (value: T) => string | undefined;
  getPrefix?: (value: T) => ReactNode;
  removeSelectedFromList?: boolean;
};

export function Dropdown<T>({
  options,
  selected,
  onChange,
  disabled = false,
  getLabel,
  getKey,
  getDescription,
  getPrefix,
  removeSelectedFromList = true,
  ...rest
}: DropdownProps<T>) {
  const prefix = selected ? getPrefix?.(selected) : undefined;
  const filteredOptions = useMemo(
    () =>
      removeSelectedFromList ? options.filter(option => selected && getKey(option) !== getKey(selected)) : options,
    [options, selected, getKey, removeSelectedFromList]
  );
  return (
    <div {...rest} style={{ display: 'contents' }}>
      <Listbox value={selected} onChange={onChange}>
        <ListboxButton
          className="border-color-border text-color-text flex h-10 min-w-[100px] items-center justify-start gap-1 rounded border p-1 font-semibold disabled:opacity-50"
          disabled={disabled}
          data-testid="dropdown-button"
        >
          {selected && (
            <>
              {prefix}
              {getLabel(selected)}
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M8 10.5L12 14.5L16 10.5"
                  stroke="#025AE9"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </>
          )}
        </ListboxButton>
        <ListboxOptions
          data-testid="dropdown-options"
          className="border-color-border bg-color-bg-main absolute z-10 mt-1 flex max-h-[50dvh] w-32 flex-col overflow-auto rounded border text-left shadow-lg"
        >
          {filteredOptions.map(item => {
            return (
              <ListboxOption
                data-testid={getKey(item)}
                key={getKey(item)}
                value={item}
                className="border-color-border flex flex-row items-start justify-start gap-1 border-b p-1 text-left hover:opacity-80 data-focus:bg-blue-100"
                as="button"
              >
                {getPrefix && getPrefix(item)}
                <div className="flex flex-col items-start gap-1">
                  {getLabel(item)}
                  <div className="text-color-text-weakest text-sm">{getDescription?.(item)}</div>
                </div>
              </ListboxOption>
            );
          })}
        </ListboxOptions>
      </Listbox>
    </div>
  );
}
